/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Building2, Globe, Phone, Mail, MapPin, ArrowLeft, Calendar,
  Heart, MessageSquare, ThumbsUp, Send, X, MoreHorizontal,
  ImageIcon, FileText, Briefcase, User, Camera, ExternalLink,
  Facebook, Instagram, Linkedin, Twitter, MessageCircle
} from 'lucide-react';
import { LeadershipBadge, LeadershipTitle } from '../../components/LeadershipBadge';
import { MultiImageUploader } from '../../components/MultiImageUploader';
import { PostImageCarousel } from '../../components/PostImageCarousel';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const avatarColors = [
  'from-[#E63946] to-[#C62D3A]',
  'from-[#457B9D] to-[#1D3557]',
  'from-[#F4A261] to-[#E76F51]',
  'from-[#2A9D8F] to-[#264653]',
  'from-[#6C5CE7] to-[#A29BFE]',
  'from-[#00B894] to-[#00CEC9]',
];
const getAvatarColor = (userId) => {
  const hash = (userId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return avatarColors[hash % avatarColors.length];
};

const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const industryLabels = {
  restaurants: 'Restaurants & Hospitality', professional: 'Professional Services', health: 'Health & Wellness',
  real_estate: 'Real Estate', finance: 'Finance', marketing: 'Marketing & Media', technology: 'Technology',
  education: 'Education', retail: 'Retail', community: 'Community Organizations', other: 'Other'
};

const MemberProfile = () => {
  const { memberId } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [commentSubmitting, setCommentSubmitting] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [newPost, setNewPost] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [repositioningBanner, setRepositioningBanner] = useState(false);
  const [bannerDragY, setBannerDragY] = useState(50);
  const bannerRef = React.useRef(null);

  const isOwnProfile = currentUser?.id === memberId;

  // Leadership profile control: leadership users cannot edit their visual identity
  const isLeadershipProfile = !!profileData?.member?.leadership_level;
  const canEditVisuals = isOwnProfile && !isLeadershipProfile;

  useEffect(() => {
    if (memberId) {
      fetchProfile();
      fetchPosts();
      fetchMedia();
    }
  }, [memberId]);  

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/portal/members/${memberId}`, { withCredentials: true });
      setProfileData(res.data);
    } catch (err) {
      toast.error('Could not load member profile');
      navigate('/portal');
    } finally { setLoading(false); }
  };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/portal/members/${memberId}/posts`, { withCredentials: true });
      setPosts(res.data);
    } catch (err) { /* */ }
  };

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API}/portal/members/${memberId}/media`, { withCredentials: true });
      setMedia(res.data);
    } catch (err) { /* */ }
  };

  const handleLike = async (postId) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.user_liked ? Math.max((p.likes_count || 0) - 1, 0) : (p.likes_count || 0) + 1, user_liked: !p.user_liked } : p));
    try { await axios.post(`${API}/portal/community/${postId}/like`, {}, { withCredentials: true }); } catch { fetchPosts(); }
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) { setExpandedComments(prev => ({ ...prev, [postId]: false })); return; }
    setExpandedComments(prev => ({ ...prev, [postId]: true }));
    if (!comments[postId]) {
      try { const res = await axios.get(`${API}/portal/community/${postId}/comments`, { withCredentials: true }); setComments(prev => ({ ...prev, [postId]: res.data })); } catch { toast.error('Failed to load comments'); }
    }
  };

  const handleComment = async (postId) => {
    const text = newComment[postId]?.trim();
    if (!text) return;
    setCommentSubmitting(prev => ({ ...prev, [postId]: true }));
    try {
      const fd = new FormData(); fd.append('content', text);
      await axios.post(`${API}/portal/community/${postId}/comments`, fd, { withCredentials: true });
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      const res = await axios.get(`${API}/portal/community/${postId}/comments`, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: res.data }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
    } catch { toast.error('Failed'); } finally { setCommentSubmitting(prev => ({ ...prev, [postId]: false })); }
  };

  const resolveImage = (img) => resolveImg(img);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/portal/profile/picture`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setProfileData(prev => prev ? { ...prev, member: { ...prev.member, picture: res.data.picture } } : prev);
      refreshUser();
      toast.success('Profile picture updated!');
    } catch { toast.error('Failed to upload'); } finally { setUploadingAvatar(false); }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingBanner(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/portal/profile/banner`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setProfileData(prev => prev ? { ...prev, member: { ...prev.member, profile_banner: res.data.banner } } : prev);
      toast.success('Banner updated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); } finally { setUploadingBanner(false); }
  };

  const handleBannerRemove = async () => {
    try {
      await axios.delete(`${API}/portal/profile/banner`, { withCredentials: true });
      setProfileData(prev => prev ? { ...prev, member: { ...prev.member, profile_banner: null, banner_position_y: null } } : prev);
      toast.success('Banner removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleBannerRepositionSave = async () => {
    try {
      await axios.put(`${API}/portal/profile/banner-position`, { position_y: bannerDragY }, { withCredentials: true });
      setProfileData(prev => prev ? { ...prev, member: { ...prev.member, banner_position_y: bannerDragY } } : prev);
      setRepositioningBanner(false);
      toast.success('Banner position saved');
    } catch { toast.error('Failed to save position'); }
  };

  const handleBannerDrag = (e) => {
    if (!repositioningBanner || !bannerRef.current) return;
    const rect = bannerRef.current.getBoundingClientRect();
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBannerDragY(Math.max(0, Math.min(100, y)));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/portal/community/upload-multi`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPostImages(prev => [...prev, ...(res.data?.image_urls || [])]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload');
    } finally { setUploading(false); }
  };

  const handlePost = async () => {
    if (!newPost.trim() && postImages.length === 0) return;
    setPosting(true);
    try {
      await axios.post(`${API}/portal/community`, { content: newPost, images: postImages, post_type: postImages.length > 0 ? 'photo' : 'update' }, { withCredentials: true });
      setNewPost(''); setPostImages([]);
      fetchPosts(); fetchMedia();
      toast.success('Posted!');
    } catch { toast.error('Failed to post'); } finally { setPosting(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>;
  if (!profileData) return null;

  const { member, company_profile: cp, membership_plan: plan, stats } = profileData;
  const allTabs = [
    { id: 'posts', label: 'Posts', icon: FileText, count: stats.posts },
    { id: 'about', label: 'About', icon: User },
    ...(!isLeadershipProfile ? [{ id: 'company', label: 'Company', icon: Building2 }] : []),
    { id: 'media', label: 'Media', icon: Camera, count: stats.media_posts + stats.gallery_images },
  ];
  const tabs = allTabs;

  return (
    <div className="min-h-screen bg-[#F0F2F5]" data-testid="member-profile-page">
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
            <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightboxImage(null)}><X className="w-8 h-8" /></button>
            <Img variant="generic" src={resolveImage(lightboxImage)} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Header */}
      <div className="bg-white shadow-sm" data-testid="profile-header">
        {/* Cover / Banner */}
        <div
          ref={bannerRef}
          className={`relative overflow-hidden group ${isLeadershipProfile ? 'h-36 sm:h-44' : 'h-32 sm:h-48'}`}
          style={!isLeadershipProfile && member.profile_banner ? { cursor: repositioningBanner ? 'ns-resize' : 'default' } : {}}
          onMouseMove={handleBannerDrag}
          data-testid="profile-banner"
        >
          {isLeadershipProfile ? (
            /* Official DCCF Leadership Banner — refined with overlay */
            <>
              <Img variant="leader"
                src="/brand/leadership-banner.jpg"
                alt="DCCF Official Banner"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: 'center 40%' }}
                draggable={false}
                loading="eager"
                data-testid="leadership-banner-image"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#001630]/30 via-transparent to-[#001630]/50" />
            </>
          ) : member.profile_banner ? (
            <Img variant="avatar"
              src={resolveImage(member.profile_banner)}
              alt="Banner"
              className="absolute inset-0 w-full h-full object-cover transition-[object-position] duration-150"
              style={{ objectPosition: `center ${member.banner_position_y ?? bannerDragY ?? 50}%` }}
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-r from-[#002249] via-[#0a3570] to-[#1D3557]">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
            </div>
          )}
          {/* Subtle bottom gradient for all banners */}
          {!isLeadershipProfile && <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-black/10" />}
          {/* Back button */}
          <button onClick={() => navigate(-1)} className="absolute top-4 left-4 flex items-center gap-1.5 text-white/90 hover:text-white text-sm font-medium bg-black/25 hover:bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg transition-colors z-20" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {/* Banner actions (own profile only, non-leadership) */}
          {canEditVisuals && !repositioningBanner && (
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <label className="flex items-center gap-1.5 text-white text-xs font-medium bg-black/40 hover:bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg cursor-pointer transition-colors" data-testid="upload-banner-btn">
                <Camera className="w-3.5 h-3.5" />{uploadingBanner ? 'Uploading...' : member.profile_banner ? 'Change Banner' : 'Add Banner'}
                <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" disabled={uploadingBanner} />
              </label>
              {member.profile_banner && (
                <>
                  <button onClick={() => { setBannerDragY(member.banner_position_y ?? 50); setRepositioningBanner(true); }}
                    className="flex items-center gap-1.5 text-white text-xs font-medium bg-black/40 hover:bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg transition-colors" data-testid="reposition-banner-btn">
                    Reposition
                  </button>
                  <button onClick={handleBannerRemove} className="flex items-center gap-1 text-white text-xs font-medium bg-red-500/70 hover:bg-red-500/90 px-2.5 py-1.5 rounded-lg transition-colors" data-testid="remove-banner-btn">
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          )}
          {/* Reposition mode */}
          {repositioningBanner && (
            <div className="absolute inset-x-0 bottom-0 bg-black/60 backdrop-blur-sm px-4 py-2 flex items-center justify-between z-20" data-testid="reposition-mode">
              <p className="text-white text-xs">Drag to reposition your banner</p>
              <div className="flex gap-2">
                <button onClick={() => setRepositioningBanner(false)} className="text-white/70 text-xs px-3 py-1 hover:text-white">Cancel</button>
                <button onClick={handleBannerRepositionSave} className="bg-white text-[#002249] text-xs font-bold px-4 py-1 rounded" data-testid="save-banner-position">Save</button>
              </div>
            </div>
          )}
          {uploadingBanner && <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-30"><div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" /></div>}
        </div>

        {/* Profile info — clean separation from banner */}
        <div className="max-w-[900px] mx-auto px-5 sm:px-8">
          <div className="relative -mt-10 sm:-mt-14 pb-5 sm:pb-6">
            {/* Top row: Avatar + Actions */}
            <div className="flex items-end justify-between mb-4 sm:mb-5">
              {/* Avatar */}
              <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full border-[3px] border-white shadow-lg flex-shrink-0 overflow-hidden relative z-10 group bg-white">
                {member.picture ? (
                  <Img variant="avatar" src={resolveImage(member.picture)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${getAvatarColor(member.id)} flex items-center justify-center text-white font-bold text-2xl sm:text-3xl`}>
                    {member.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                {canEditVisuals && (
                  <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center cursor-pointer">
                    <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-upload" />
                  </label>
                )}
                {uploadingAvatar && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full" /></div>}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 pt-10 sm:pt-14">
                {!isOwnProfile && (
                  <Link to={`/portal/messages?to=${member.id}`}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-[#E63946] hover:bg-[#D32F3F] px-4 py-2.5 rounded-lg transition-colors shadow-sm" data-testid="message-member-btn">
                    <MessageCircle className="w-3.5 h-3.5" /> Message
                  </Link>
                )}
                {isOwnProfile && !isLeadershipProfile && (
                  <Link to="/portal/social-profile" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#002249] border border-[#002249]/15 bg-white px-4 py-2.5 rounded-lg hover:bg-[#002249] hover:text-white transition-all shadow-sm" data-testid="edit-profile-btn">
                    Edit Profile
                  </Link>
                )}
                {isLeadershipProfile && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#002249] bg-[#002249]/5 border border-[#002249]/10 px-3.5 py-2.5 rounded-lg" data-testid="official-profile-badge">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Official DCCF Profile
                  </span>
                )}
                {cp?.company_name && !isLeadershipProfile && (
                  <Link to={`/portal/business/${memberId}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[#002249] hover:bg-[#001630] px-3.5 py-2.5 rounded-lg transition-colors shadow-sm" data-testid="view-business-profile-btn">
                    <Building2 className="w-3.5 h-3.5" /> Business Profile
                  </Link>
                )}
              </div>
            </div>

            {/* Name & details */}
            <div className="space-y-2">
              {/* Name */}
              <div className="flex items-center gap-2.5 flex-wrap" data-testid="member-name">
                <h1 className="text-[22px] sm:text-[26px] font-bold text-[#1A1A1A] leading-tight tracking-[-0.02em]">
                  {member.display_name || member.name}
                </h1>
                {member.leadership_level && <LeadershipBadge badges={member.badges} leadershipLevel={member.leadership_level} leadershipTitle={member.leadership_title} size="md" />}
              </div>

              {/* Title / Role */}
              {member.leadership_level && <LeadershipTitle badges={member.badges} leadershipLevel={member.leadership_level} leadershipTitle={member.leadership_title} className="block" />}
              {member.title && !member.leadership_level && <p className="text-[13px] text-[#E63946] font-medium">{member.title}</p>}

              {/* Company info or linked company */}
              {cp?.company_name && (
                <p className="text-[13px] text-[#555] flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 flex-shrink-0 text-[#8C8C8C]" /> {cp.company_name}
                  {cp.industry && <span className="text-[#C0C0C0]">&middot;</span>}
                  {cp.industry && <span className="text-[#8C8C8C]">{industryLabels[cp.industry] || cp.industry}</span>}
                </p>
              )}
              {!cp?.company_name && profileData?.linked_company && (
                <Link to={`/portal/business/${profileData.linked_company.user_id}`} className="text-[13px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 transition-colors" data-testid="linked-company">
                  <Building2 className="w-3.5 h-3.5 flex-shrink-0" /> Represents {profileData.linked_company.company_name}
                </Link>
              )}

              {/* Roles */}
              {member.roles?.length > 0 && (
                <div className="flex items-center flex-wrap gap-1.5 pt-0.5">
                  {member.roles.map((r, i) => (
                    <span key={i} className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium text-[11px] capitalize">{r}</span>
                  ))}
                </div>
              )}

              {/* Meta badges */}
              <div className="flex items-center flex-wrap gap-2.5 pt-1.5 text-[12px] text-[#8C8C8C]">
                {member.is_founding_member && (
                  <span className="bg-amber-50 text-amber-700 border border-amber-200/80 px-2.5 py-[3px] rounded-full font-semibold text-[11px] flex items-center gap-1" data-testid="founding-member-badge">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    Founding Member
                  </span>
                )}
                {plan && <span className="bg-[#002249]/6 text-[#002249] px-2.5 py-[3px] rounded-full font-semibold text-[11px]" data-testid="member-plan">{plan.name}</span>}
                {member.chamber_role && !plan && (
                  <span className="bg-indigo-50 text-indigo-600 px-2.5 py-[3px] rounded-full font-semibold text-[11px] capitalize" data-testid="chamber-role-badge">{member.chamber_role} — DCCF</span>
                )}
                {stats.joined && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Since {new Date(stats.joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                <span className="text-[#1A1A1A] font-semibold">{stats.posts} <span className="font-normal text-[#8C8C8C]">posts</span></span>
                <span className="text-[#1A1A1A] font-semibold">{stats.media_posts + stats.gallery_images} <span className="font-normal text-[#8C8C8C]">photos</span></span>
              </div>

              {/* Bio */}
              {member.bio && <p className="text-[14px] text-[#555] pt-1 line-clamp-2 max-w-2xl leading-[1.65]">{member.bio}</p>}
              {!member.bio && cp?.description && <p className="text-[14px] text-[#555] pt-1 line-clamp-2 max-w-2xl leading-[1.65]">{cp.description}</p>}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-[#E4E6EB] -mx-5 sm:-mx-8 px-5 sm:px-8 overflow-x-auto" data-testid="profile-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-[14px] font-semibold border-b-[3px] transition-colors whitespace-nowrap ${activeTab === tab.id ? 'text-[#002249] border-[#002249]' : 'text-[#65676B] border-transparent hover:bg-[#F0F2F5]'}`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && <span className="text-[12px] bg-[#E4E6EB] text-[#65676B] rounded-full px-1.5">{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-5">
        {/* === POSTS TAB === */}
        {activeTab === 'posts' && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
            <div className="space-y-4">
              {/* Post Composer (own profile only) */}
              {isOwnProfile && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="profile-post-composer">
                  <div className="p-4">
                    <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                      placeholder="Share an update..."
                      rows={newPost || postImages.length > 0 ? 3 : 1}
                      className="w-full bg-[#F0F2F5] rounded-xl px-4 py-2.5 text-[14px] placeholder:text-[#65676B] text-[#1C1E21] resize-none border-0 focus:outline-none focus:bg-[#E8EAED] transition-colors"
                      data-testid="profile-post-input" />
                    {postImages.length > 0 && (
                      <div className="mt-3">
                        <MultiImageUploader
                          value={postImages}
                          onChange={setPostImages}
                          max={5}
                          label="Add more photos"
                          size="sm"
                          dataTestId="profile-composer-uploader"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between px-4 py-2 border-t border-[#E4E6EB]">
                    {postImages.length === 0 ? (
                      <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[#65676B] hover:bg-[#F0F2F5] cursor-pointer transition-colors text-[13px]" data-testid="profile-image-upload">
                        <ImageIcon className="w-4 h-4 text-[#45BD62]" />
                        <span className="hidden sm:inline">{uploading ? 'Uploading...' : 'Photos'}</span>
                        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                      </label>
                    ) : (
                      <span className="text-[12px] text-[#65676B] font-medium" data-testid="profile-composer-count">
                        {postImages.length} / 5 photo{postImages.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <button onClick={handlePost} disabled={posting || (!newPost.trim() && postImages.length === 0)}
                      className="px-4 py-1.5 bg-[#E63946] hover:bg-[#D32F3F] disabled:bg-[#E4E6EB] disabled:text-[#BCC0C4] text-white text-[13px] font-semibold rounded-lg transition-colors" data-testid="profile-post-btn">
                      {posting ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>
              )}
              {posts.length > 0 ? posts.map((post, index) => (
                <PostCard key={post.id} post={post} index={index} currentUser={currentUser}
                  onLike={handleLike} onToggleComments={toggleComments}
                  expandedComments={expandedComments} comments={comments}
                  newComment={newComment} setNewComment={setNewComment}
                  onComment={handleComment} commentSubmitting={commentSubmitting}
                  resolveImage={resolveImage} onOpenLightbox={setLightboxImage} />
              )) : (
                <div className="bg-white rounded-xl shadow-sm p-10 text-center">
                  <FileText className="w-12 h-12 text-[#BCC0C4] mx-auto mb-3" />
                  <h3 className="text-[16px] font-semibold text-[#050505]">No posts yet</h3>
                  <p className="text-[14px] text-[#65676B] mt-1">{isOwnProfile ? 'Share your first update with the community!' : `${member.name?.split(' ')[0]} hasn't posted yet.`}</p>
                </div>
              )}
            </div>
            {/* Sidebar info */}
            <div className="hidden lg:block space-y-4">
              <AboutCard cp={cp} member={member} plan={plan} resolveImage={resolveImage} compact />
            </div>
          </div>
        )}

        {/* === ABOUT TAB === */}
        {activeTab === 'about' && (
          <div className="max-w-2xl space-y-4">
            <AboutCard cp={cp} member={member} plan={plan} resolveImage={resolveImage} />
          </div>
        )}

        {/* === COMPANY TAB === */}
        {activeTab === 'company' && (
          <div className="max-w-2xl space-y-4">
            <CompanyCard cp={cp} member={member} resolveImage={resolveImage} />
          </div>
        )}

        {/* === MEDIA TAB === */}
        {activeTab === 'media' && (
          <div>
            {media.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {media.map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }}
                    className="relative aspect-square group cursor-pointer rounded-lg overflow-hidden bg-[#E4E6EB]"
                    onClick={() => setLightboxImage(item.url)} data-testid={`media-item-${i}`}>
                    <Img variant="generic" src={resolveImage(item.url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    <span className="absolute bottom-1.5 left-1.5 text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      {item.source === 'gallery' ? 'Gallery' : 'Post'}
                    </span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-10 text-center">
                <Camera className="w-12 h-12 text-[#BCC0C4] mx-auto mb-3" />
                <h3 className="text-[16px] font-semibold text-[#050505]">No media yet</h3>
                <p className="text-[14px] text-[#65676B] mt-1">{isOwnProfile ? 'Upload photos to your posts or gallery.' : 'No photos shared yet.'}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ========== SUB-COMPONENTS ========== */

const PostCard = ({ post, index, currentUser, onLike, onToggleComments, expandedComments, comments, newComment, setNewComment, onComment, commentSubmitting, resolveImage, onOpenLightbox }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.2) }}
    className="bg-white rounded-xl shadow-sm" data-testid={`profile-post-${index}`}>
    {post.is_official && <div className="bg-[#002249] h-1 rounded-t-xl" />}
    <div className="flex items-center justify-between px-4 pt-3 pb-1">
      <div className="flex items-center gap-2.5">
        {post.is_official ? (
          <div className="w-10 h-10 rounded-full bg-[#002249] flex items-center justify-center text-white font-semibold text-sm shadow-sm">D</div>
        ) : post.author?.picture ? (
          <Img variant="avatar"
            src={resolveImage(post.author.picture)}
            alt={post.author?.name || ''}
            className="w-10 h-10 rounded-full object-cover shadow-sm bg-slate-100"
          />
        ) : (
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(post.user_id)} flex items-center justify-center text-white font-semibold text-sm shadow-sm`}>
            {post.author?.name?.charAt(0).toUpperCase() || '?'}
          </div>
        )}
        <div>
          <p className="text-[15px] font-semibold text-[#050505] leading-tight">{post.is_official ? (post.official_label || 'DCCF') : (post.author?.name || 'Member')}</p>
          <div className="flex items-center gap-1 text-[12px] text-[#65676B]">
            {!post.is_official && post.company?.name && <><span>{post.company.name}</span><span className="mx-0.5">&middot;</span></>}
            <span>{getTimeAgo(post.created_at)}</span>
          </div>
        </div>
      </div>
    </div>
    <div className="px-4 py-2"><p className="text-[15px] text-[#050505] leading-[1.35] whitespace-pre-wrap">{post.content}</p></div>
    {post.images?.length > 0 && (
      <PostImageCarousel
        images={post.images}
        maxHeight="max-h-[500px]"
        onImageClick={(url) => onOpenLightbox?.(url)}
      />
    )}
    {((post.likes_count || 0) > 0 || (post.comments_count || 0) > 0) && (
      <div className="flex items-center justify-between px-4 py-2 text-[13px] text-[#65676B]">
        {(post.likes_count || 0) > 0 ? <div className="flex items-center gap-1"><span className="w-[18px] h-[18px] bg-[#E63946] rounded-full flex items-center justify-center"><ThumbsUp className="w-[10px] h-[10px] text-white" /></span><span>{post.likes_count}</span></div> : <div />}
        {(post.comments_count || 0) > 0 && <button onClick={() => onToggleComments(post.id)} className="hover:underline">{post.comments_count} comment{post.comments_count !== 1 ? 's' : ''}</button>}
      </div>
    )}
    <div className="mx-4 py-1 border-t border-[#E4E6EB] flex">
      <button onClick={() => onLike(post.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-[6px] rounded-md text-[14px] font-semibold transition-colors ${post.user_liked ? 'text-[#E63946] hover:bg-[#FEF2F2]' : 'text-[#65676B] hover:bg-[#F0F2F5]'}`}>
        <Heart className={`w-[18px] h-[18px] ${post.user_liked ? 'fill-current' : ''}`} /><span>{post.user_liked ? 'Liked' : 'Like'}</span>
      </button>
      <button onClick={() => onToggleComments(post.id)} className="flex-1 flex items-center justify-center gap-1.5 py-[6px] rounded-md text-[14px] font-semibold text-[#65676B] hover:bg-[#F0F2F5] transition-colors">
        <MessageSquare className="w-[18px] h-[18px]" /><span>Comment</span>
      </button>
    </div>
    <AnimatePresence>
      {expandedComments[post.id] && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          <div className="px-4 pb-3 pt-1">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarColor(currentUser?.id)} flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0`}>
                {currentUser?.name?.charAt(0).toUpperCase() || 'M'}
              </div>
              <div className="flex-1 flex items-center bg-[#F0F2F5] rounded-2xl overflow-hidden">
                <input type="text" value={newComment[post.id] || ''} onChange={e => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                  placeholder="Write a comment..." className="flex-1 bg-transparent px-3 py-2 text-[13px] text-[#050505] placeholder:text-[#65676B] focus:outline-none"
                  onKeyDown={e => e.key === 'Enter' && onComment(post.id)} />
                <button onClick={() => onComment(post.id)} disabled={commentSubmitting[post.id] || !newComment[post.id]?.trim()} className="px-3 py-2 text-[#E63946] disabled:text-[#BCC0C4]">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-2.5">
              {comments[post.id]?.map((comment, ci) => (
                <div key={comment.id} className="flex items-start gap-2">
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarColor(comment.user_id)} flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0`}>
                    {comment.author?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="bg-[#F0F2F5] rounded-2xl px-3 py-2 inline-block">
                      <p className="text-[13px] font-semibold text-[#050505] leading-tight">{comment.author?.name || 'Member'}</p>
                      <p className="text-[13px] text-[#050505] mt-0.5">{comment.content}</p>
                    </div>
                    <p className="text-[11px] text-[#65676B] mt-0.5 ml-3">{getTimeAgo(comment.created_at)}</p>
                  </div>
                </div>
              ))}
              {(!comments[post.id] || comments[post.id].length === 0) && <p className="text-[13px] text-[#65676B] text-center py-1">No comments yet</p>}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const AboutCard = ({ cp, member, plan, resolveImage, compact }) => (
  <div className="bg-white rounded-xl shadow-sm p-5" data-testid="about-section">
    <h3 className="text-[16px] font-bold text-[#050505] mb-4">{compact ? 'About' : 'About This Member'}</h3>
    <div className="space-y-3">
      {cp?.description && <p className="text-[14px] text-[#050505] leading-relaxed">{cp.description}</p>}
      {cp?.vision && !compact && (
        <div><h4 className="text-[13px] font-semibold text-[#65676B] uppercase tracking-wide mb-1">Vision</h4><p className="text-[14px] text-[#050505]">{cp.vision}</p></div>
      )}
      {cp?.mission && !compact && (
        <div><h4 className="text-[13px] font-semibold text-[#65676B] uppercase tracking-wide mb-1">Mission</h4><p className="text-[14px] text-[#050505]">{cp.mission}</p></div>
      )}
      <div className="pt-2 space-y-2.5">
        {cp?.company_name && <InfoRow icon={Building2} label={cp.company_name} />}
        {cp?.industry && <InfoRow icon={Briefcase} label={industryLabels[cp.industry] || cp.industry} />}
        {plan && <InfoRow icon={User} label={`${plan.name} Member`} />}
        {member.chamber_role && !plan && <InfoRow icon={User} label={<span className="capitalize">{member.chamber_role} — DCCF</span>} />}
        {cp?.address && <InfoRow icon={MapPin} label={cp.address} />}
        {cp?.website && <InfoRow icon={Globe} label={<a href={cp.website.startsWith('http') ? cp.website : `https://${cp.website}`} target="_blank" rel="noopener noreferrer" className="text-[#002249] hover:underline">{cp.website}</a>} />}
        {cp?.email && !compact && <InfoRow icon={Mail} label={cp.email} />}
        {cp?.phone && !compact && <InfoRow icon={Phone} label={cp.phone} />}
      </div>
      {!compact && (cp?.social_facebook || cp?.social_instagram || cp?.social_linkedin || cp?.social_twitter) && (
        <div className="flex items-center gap-3 pt-3 border-t border-[#E4E6EB]">
          {cp.social_facebook && <SocialLink href={cp.social_facebook} icon={Facebook} color="text-blue-600" />}
          {cp.social_instagram && <SocialLink href={cp.social_instagram} icon={Instagram} color="text-pink-600" />}
          {cp.social_linkedin && <SocialLink href={cp.social_linkedin} icon={Linkedin} color="text-blue-700" />}
          {cp.social_twitter && <SocialLink href={cp.social_twitter} icon={Twitter} color="text-sky-500" />}
        </div>
      )}
    </div>
  </div>
);

const CompanyCard = ({ cp, member, resolveImage }) => {
  if (!cp || !cp.company_name) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-10 text-center">
        <Building2 className="w-12 h-12 text-[#BCC0C4] mx-auto mb-3" />
        <h3 className="text-[16px] font-semibold text-[#050505]">No company profile yet</h3>
        <p className="text-[14px] text-[#65676B] mt-1">This member hasn't set up their company information.</p>
      </div>
    );
  }

  return (
    <>
      {/* Company header */}
      <div className="bg-white rounded-xl shadow-sm p-5" data-testid="company-section">
        <div className="flex items-start gap-4">
          {cp.logo_url ? (
            <Img variant="business" src={resolveImage(cp.logo_url)} alt="" className="w-20 h-20 rounded-lg object-contain bg-[#F0F2F5] p-2 flex-shrink-0" />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-[#F0F2F5] flex items-center justify-center flex-shrink-0"><Building2 className="w-8 h-8 text-[#BCC0C4]" /></div>
          )}
          <div>
            <h3 className="text-lg font-bold text-[#050505]">{cp.company_name}</h3>
            {cp.slogan && <p className="text-[14px] text-[#65676B] italic mt-0.5">"{cp.slogan}"</p>}
            {cp.industry && <span className="inline-block mt-2 text-[12px] bg-[#E4E6EB] text-[#65676B] px-2.5 py-0.5 rounded-full">{industryLabels[cp.industry] || cp.industry}</span>}
          </div>
        </div>
        {cp.description && <p className="text-[14px] text-[#050505] mt-4 leading-relaxed">{cp.description}</p>}
      </div>

      {/* Contact details */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h4 className="text-[15px] font-bold text-[#050505] mb-3">Contact Information</h4>
        <div className="space-y-2.5">
          {cp.contact_person && <InfoRow icon={User} label={`${cp.contact_person} (Contact Person)`} />}
          {cp.email && <InfoRow icon={Mail} label={cp.email} />}
          {cp.phone && <InfoRow icon={Phone} label={cp.phone} />}
          {cp.address && <InfoRow icon={MapPin} label={cp.address} />}
          {cp.website && <InfoRow icon={Globe} label={<a href={cp.website.startsWith('http') ? cp.website : `https://${cp.website}`} target="_blank" rel="noopener noreferrer" className="text-[#002249] hover:underline flex items-center gap-1">{cp.website} <ExternalLink className="w-3 h-3" /></a>} />}
        </div>
      </div>

      {/* Gallery */}
      {cp.gallery_images?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h4 className="text-[15px] font-bold text-[#050505] mb-3">Photo Gallery</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {cp.gallery_images.map((img, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden bg-[#E4E6EB]">
                <Img variant="generic" src={resolveImage(img)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const InfoRow = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2.5 text-[14px] text-[#050505]">
    <Icon className="w-4 h-4 text-[#65676B] flex-shrink-0" />
    <span>{label}</span>
  </div>
);

const SocialLink = ({ href, icon: Icon, color }) => (
  <a href={href.startsWith('http') ? href : `https://${href}`} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 rounded-full bg-[#F0F2F5] flex items-center justify-center ${color} hover:bg-[#E4E6EB] transition-colors`}>
    <Icon className="w-4 h-4" />
  </a>
);

export default MemberProfile;
